// app/javascript/pages/Student/Notifications/Index.tsx
import type React from 'react'
import { router } from '@inertiajs/react'
import { StudentShell } from '@/components/shared/StudentShell'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Bell, Coins, Trophy, Medal, Info, CheckCheck, Clock, Target } from 'lucide-react'
import type { NotificationItem } from '@/types'

const KIND_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  credit_granted: Coins,
  new_test: Trophy,
  badge_earned: Medal,
  exam_enrolled: Target,
  exam_unenrolled: Target,
  system: Info,
}

const KIND_COLORS: Record<string, string> = {
  credit_granted: 'bg-tertiary/10 text-tertiary border-tertiary/20',
  new_test: 'bg-primary/10 text-primary border-primary/20',
  badge_earned: 'bg-secondary/10 text-secondary border-secondary/20',
  exam_enrolled: 'bg-primary/10 text-primary border-primary/20',
  exam_unenrolled: 'bg-muted text-muted-foreground border-border',
  system: 'bg-muted text-muted-foreground border-border',
}

interface Props {
  notifications: NotificationItem[]
  unread_count: number
}

// Clean any leading raw emoji from server titles so we present consistent vector typography
function cleanNotificationTitle(title: string): string {
  if (!title) return ''
  return title.replace(/^[\p{Extended_Pictographic}\uFE0F\s]+/u, '').trim() || title
}

export default function NotificationsIndex({ notifications, unread_count }: Props) {
  const handleMarkRead = (id: number) => {
    router.patch(`/student/notifications/${id}/read`, {}, { preserveScroll: true })
  }

  const handleMarkAllRead = () => {
    router.patch('/student/notifications/read_all', {}, { preserveScroll: true })
  }

  const readCount = notifications.length - unread_count

  return (
    <StudentShell>
      <div className="space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-16">
        {/* Hero Card Banner */}
        <div className="rounded-2xl border border-border/70 bg-card p-3.5 sm:p-6 lg:p-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div className="space-y-2 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-2.5 py-0.5 rounded-full border border-primary/25 inline-flex items-center gap-1.5 shadow-2xs">
                  <Bell className="h-3 w-3 text-primary" />
                  <span>Activity Feed</span>
                </span>
                {unread_count > 0 && (
                  <Badge variant="destructive" className="text-[10px] font-extrabold px-2 py-0.5 rounded-full">
                    {unread_count} Unread
                  </Badge>
                )}
              </div>

              <h1 className="text-lg sm:text-2xl font-bold text-foreground tracking-tight">
                Notifications & Alerts
              </h1>

              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Stay updated with your latest mock test evaluations, leaderboard rankings, and daily credit grants.
              </p>
            </div>

            {/* Quick Metrics Strip & Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
              <div className="grid grid-cols-3 gap-2 sm:gap-2.5 shrink-0 w-full sm:w-auto sm:min-w-[240px]">
                <div className="flex flex-col items-center justify-center py-2.5 px-1 sm:p-3 rounded-xl bg-muted/40 border border-border/60 text-center">
                  <span className="text-base sm:text-lg font-black text-foreground">{notifications.length}</span>
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mt-0.5">Total</span>
                </div>
                <div className="flex flex-col items-center justify-center py-2.5 px-1 sm:p-3 rounded-xl bg-primary/10 border border-primary/20 text-center">
                  <span className="text-base sm:text-lg font-black text-primary">{unread_count}</span>
                  <span className="text-[10px] font-bold text-primary uppercase tracking-wider mt-0.5">Unread</span>
                </div>
                <div className="flex flex-col items-center justify-center py-2.5 px-1 sm:p-3 rounded-xl bg-secondary/10 border border-secondary/20 text-center">
                  <span className="text-base sm:text-lg font-black text-foreground">{readCount}</span>
                  <span className="text-[10px] font-bold text-secondary uppercase tracking-wider mt-0.5">Read</span>
                </div>
              </div>

              {unread_count > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkAllRead}
                  className="w-full sm:w-auto rounded-xl font-bold text-xs h-9 sm:h-10 border-border text-foreground hover:bg-primary/10 hover:border-primary/40 shadow-xs shrink-0 cursor-pointer"
                >
                  <CheckCheck className="h-3.5 w-3.5 mr-1 text-primary" /> Mark all read
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <Card className="rounded-3xl border border-border/80 bg-card/60 backdrop-blur-glass shadow-glass-sm p-10 sm:p-14 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted/60 text-muted-foreground flex items-center justify-center mx-auto mb-3 border border-border/60">
              <Bell className="h-7 w-7" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-foreground">No notifications yet</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-sm mx-auto leading-relaxed">
              When you earn credits, unlock achievement badges, or complete mock tests, updates will appear here.
            </p>
          </Card>
        ) : (
          <div className="space-y-2.5 sm:space-y-3">
            {notifications.map((n) => {
              const Icon = KIND_ICONS[n.kind] || Info
              const colorClass = KIND_COLORS[n.kind] || KIND_COLORS.system
              const title = cleanNotificationTitle(n.title)

              return (
                <div
                  key={n.id}
                  onClick={() => !n.read && handleMarkRead(n.id)}
                  className={`flex gap-3 sm:gap-3.5 p-3 sm:p-4 rounded-2xl border transition-all cursor-pointer ${
                    n.read
                      ? 'border-border/60 bg-card hover:border-border shadow-2xs opacity-85 hover:opacity-100'
                      : 'border-primary/35 bg-primary/5 hover:bg-primary/10 shadow-glass-xs'
                  }`}
                >
                  {/* Category Icon */}
                  <div
                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 border ${colorClass}`}
                  >
                    <Icon className="h-4.5 w-4.5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-xs sm:text-sm font-extrabold ${!n.read ? 'text-foreground' : 'text-foreground/80'}`}>
                        {title}
                      </p>
                      {!n.read && (
                        <span className="flex h-2 w-2 rounded-full bg-primary shrink-0 mt-1 shadow-xs" title="Unread" />
                      )}
                    </div>

                    {n.body && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                        {n.body}
                      </p>
                    )}

                    <div className="flex items-center gap-1 text-[10px] sm:text-[11px] text-muted-foreground/80 font-medium mt-1.5">
                      <Clock className="h-3 w-3 text-muted-foreground/60" />
                      <span>{n.created_at}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </StudentShell>
  )
}
